/**
 * Mapeo del tipo de negocio (categoria/industria de OpenStreetMap, REAL) a la
 * categoria de actividad de Google Analytics (pagePath, REAL). Es lo que
 * permite cruzar cada POI con la demanda web medida.
 *
 * La clasificacion usa la categoria/industria de OSM, no el `traveler_type` del
 * Data Pack (que es simulado para Levi). Asi el lado izquierdo del cruce es
 * dato real y solo la demanda (GA) aporta la magnitud.
 */
const CATEGORY_TO_ACTIVITY: Record<string, string> = {
  // Alojamiento
  chalet: 'accommodation',
  apartment: 'accommodation',
  hotel: 'accommodation',
  guest_house: 'accommodation',
  hostel: 'accommodation',
  motel: 'accommodation',
  // Comida y bebida
  restaurant: 'food_drink',
  cafe: 'food_drink',
  bar: 'food_drink',
  pub: 'food_drink',
  fast_food: 'food_drink',
  // Actividades / experiencias
  attraction: 'activities',
  viewpoint: 'activities',
  // Informacion practica
  tourist_information: 'info_practical',
}

export function poiActivity(category?: string, industry?: string): string {
  const c = (category ?? '').toLowerCase()
  if (CATEGORY_TO_ACTIVITY[c]) return CATEGORY_TO_ACTIVITY[c]

  const i = (industry ?? '').toLowerCase()
  if (i.includes('food') || i.includes('beverage')) return 'food_drink'
  if (i.includes('rental') || i.includes('accommod')) return 'accommodation'
  if (i.includes('attraction') || i.includes('activit')) return 'activities'
  return 'other'
}

/** Nombre del pais en ISO2, para etiquetas cortas. */
export function countryName(iso2: string, locale: string): string {
  const names: Record<string, Record<string, string>> = {
    FI: { es: 'Finlandia', en: 'Finland', fi: 'Suomi' },
    GB: { es: 'Reino Unido', en: 'United Kingdom', fi: 'Iso-Britannia' },
    DE: { es: 'Alemania', en: 'Germany', fi: 'Saksa' },
    FR: { es: 'Francia', en: 'France', fi: 'Ranska' },
    NL: { es: 'Países Bajos', en: 'Netherlands', fi: 'Alankomaat' },
    SE: { es: 'Suecia', en: 'Sweden', fi: 'Ruotsi' },
    CH: { es: 'Suiza', en: 'Switzerland', fi: 'Sveitsi' },
    NO: { es: 'Noruega', en: 'Norway', fi: 'Norja' },
    US: { es: 'EE. UU.', en: 'United States', fi: 'Yhdysvallat' },
    AT: { es: 'Austria', en: 'Austria', fi: 'Itävalta' },
    PL: { es: 'Polonia', en: 'Poland', fi: 'Puola' },
    ES: { es: 'España', en: 'Spain', fi: 'Espanja' },
    IT: { es: 'Italia', en: 'Italy', fi: 'Italia' },
    DK: { es: 'Dinamarca', en: 'Denmark', fi: 'Tanska' },
    BE: { es: 'Bélgica', en: 'Belgium', fi: 'Belgia' },
    IE: { es: 'Irlanda', en: 'Ireland', fi: 'Irlanti' },
    EE: { es: 'Estonia', en: 'Estonia', fi: 'Viro' },
    LV: { es: 'Letonia', en: 'Latvia', fi: 'Latvia' },
    LT: { es: 'Lituania', en: 'Lithuania', fi: 'Liettua' },
    CZ: { es: 'Chequia', en: 'Czechia', fi: 'Tšekki' },
    HU: { es: 'Hungría', en: 'Hungary', fi: 'Unkari' },
    RO: { es: 'Rumanía', en: 'Romania', fi: 'Romania' },
    SI: { es: 'Eslovenia', en: 'Slovenia', fi: 'Slovenia' },
    SK: { es: 'Eslovaquia', en: 'Slovakia', fi: 'Slovakia' },
    CA: { es: 'Canadá', en: 'Canada', fi: 'Kanada' },
    AU: { es: 'Australia', en: 'Australia', fi: 'Australia' },
    SG: { es: 'Singapur', en: 'Singapore', fi: 'Singapore' },
    JP: { es: 'Japón', en: 'Japan', fi: 'Japani' },
    CN: { es: 'China', en: 'China', fi: 'Kiina' },
    KR: { es: 'Corea del Sur', en: 'South Korea', fi: 'Etelä-Korea' },
    RU: { es: 'Rusia', en: 'Russia', fi: 'Venäjä' },
    BR: { es: 'Brasil', en: 'Brazil', fi: 'Brasilia' },
    IN: { es: 'India', en: 'India', fi: 'Intia' },
    PT: { es: 'Portugal', en: 'Portugal', fi: 'Portugali' },
    GR: { es: 'Grecia', en: 'Greece', fi: 'Kreikka' },
    TR: { es: 'Turquía', en: 'Türkiye', fi: 'Turkki' },
    IL: { es: 'Israel', en: 'Israel', fi: 'Israel' },
    IS: { es: 'Islandia', en: 'Iceland', fi: 'Islanti' },
    TH: { es: 'Tailandia', en: 'Thailand', fi: 'Thaimaa' },
  }
  return names[iso2]?.[locale] ?? iso2
}